import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styles from "./AdminShared.module.css";
import local from "./AdminTracking.module.css";
import { useAdminUI } from "./AdminUIContext";
import { downloadJson, formatDateDisplay, paginate } from "./adminUtils";
import {
  fetchTrackEvents,
  fetchTrackSummary,
  trackSnapshotUrl,
} from "../../../storage/trackDB";
import {
  buildUtmUrl,
  slugUtmValue,
  utmVocabulary,
} from "../../../data/utmTracking";
import AdminTrackBrowserSetting from "./AdminTrackBrowserSetting";
import AdminTrackCountAdjust from "./AdminTrackCountAdjust";
import {
  EMPTY_FILTERS,
  NONE_ID,
  aggregateTrackRows,
  formatConversion,
  hasActiveFilters,
  rowMatchesFilters,
} from "./trackSummaryView";
import { presetYmdRange, rangeQueryParams } from "./trackRange";

const RANGE_OPTIONS = [
  { id: 7, label: "7 days" },
  { id: 30, label: "30 days" },
  { id: "all", label: "All" },
];

const emptySummary = {
  generatedAt: null,
  rangeDays: 30,
  totals: { landings: 0, signups: 0, conversion: 0 },
  rawTotals: { landings: 0, signups: 0, conversion: 0 },
  adjustment: {
    landingsDelta: 0,
    signupsDelta: 0,
    note: "",
    updatedAt: null,
    active: false,
  },
  bySource: [],
  byMedium: [],
  byCampaign: [],
  byContent: [],
  rows: [],
};

const DIM_VOCAB = {
  source: utmVocabulary.sources || [],
  medium: utmVocabulary.mediums || [],
  campaign: utmVocabulary.campaigns || [],
  content: [],
};

const formatCount = (n) => (Number.isFinite(n) ? n.toLocaleString() : "0");

const dimDisplay = (key, id) => {
  if (!id || id === NONE_ID) return "—";
  const known = (DIM_VOCAB[key] || []).find((item) => item.id === id);
  return known?.label || id;
};

const emptyHits = { events: [], total: 0, truncated: false };
export const HITS_PAGE_SIZE = 20;

const eventLabel = (event) => {
  if (event === "landing") return "Landing";
  if (event === "signup") return "Signup";
  return event || "—";
};

const NEW_CAMPAIGN = "__new__";

const campaignChoices = (byCampaign) => {
  const map = new Map();
  (utmVocabulary.campaigns || []).forEach((item) => {
    if (item?.id) map.set(item.id, item.label || item.id);
  });
  (byCampaign || []).forEach((item) => {
    if (item?.id && item.id !== NONE_ID && !map.has(item.id)) {
      map.set(item.id, item.id);
    }
  });
  return [...map.entries()].map(([id, label]) => ({ id, label }));
};

const AdminTracking = () => {
  const { toast } = useAdminUI();
  const user = useSelector((state) => state.auth.user);
  const initialRange = presetYmdRange(30);
  const [days, setDays] = useState(30);
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [summary, setSummary] = useState(emptySummary);
  const [hits, setHits] = useState(emptyHits);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [hitsPage, setHitsPage] = useState(1);
  const [builder, setBuilder] = useState({
    source: "x",
    medium: "social",
    campaign: utmVocabulary.defaultCampaign,
    customCampaign: false,
    content: "t-01",
  });

  const queryParams = useMemo(
    () => rangeQueryParams({ days, fromDate, toDate }),
    [days, fromDate, toDate],
  );

  const applyLoaded = (summaryData, hitsData) => {
    setSummary({ ...emptySummary, ...summaryData });
    setHits({ ...emptyHits, ...hitsData });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryData, hitsData] = await Promise.all([
        fetchTrackSummary(user, queryParams),
        fetchTrackEvents(user, queryParams).catch(() => emptyHits),
      ]);
      applyLoaded(summaryData, hitsData);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load tracking data.",
      );
      setSummary(emptySummary);
      setHits(emptyHits);
    } finally {
      setLoading(false);
    }
  }, [user, queryParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([
      fetchTrackSummary(user, queryParams),
      fetchTrackEvents(user, queryParams).catch(() => emptyHits),
    ])
      .then(([summaryData, hitsData]) => {
        if (cancelled) return;
        applyLoaded(summaryData, hitsData);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Could not load tracking data.",
        );
        setSummary(emptySummary);
        setHits(emptyHits);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, queryParams]);

  useEffect(() => {
    const onRefresh = () => {
      Promise.all([
        fetchTrackSummary(user, queryParams),
        fetchTrackEvents(user, queryParams).catch(() => emptyHits),
      ])
        .then(([summaryData, hitsData]) => {
          applyLoaded(summaryData, hitsData);
          setError("");
        })
        .catch(() => {});
    };
    window.addEventListener("ms1-admin-refresh-tracking", onRefresh);
    return () =>
      window.removeEventListener("ms1-admin-refresh-tracking", onRefresh);
  }, [user, queryParams]);

  const view = useMemo(
    () => aggregateTrackRows(summary.rows, filters),
    [summary.rows, filters],
  );
  const allView = useMemo(
    () => aggregateTrackRows(summary.rows, EMPTY_FILTERS),
    [summary.rows],
  );
  const sourceItems = useMemo(
    () => aggregateTrackRows(summary.rows, filters, "source").bySource,
    [summary.rows, filters],
  );
  const mediumItems = useMemo(
    () => aggregateTrackRows(summary.rows, filters, "medium").byMedium,
    [summary.rows, filters],
  );
  const campaignItems = useMemo(
    () => aggregateTrackRows(summary.rows, filters, "campaign").byCampaign,
    [summary.rows, filters],
  );
  const contentItems = useMemo(
    () => aggregateTrackRows(summary.rows, filters, "content").byContent,
    [summary.rows, filters],
  );

  const hasFilters = hasActiveFilters(filters);
  const filteredRows = view.rows;
  const filteredTotals = view.totals;
  const filteredHits = useMemo(
    () => (hits.events || []).filter((hit) => rowMatchesFilters(hit, filters)),
    [hits.events, filters],
  );
  const hitsPager = useMemo(
    () => paginate(filteredHits, hitsPage, HITS_PAGE_SIZE),
    [filteredHits, hitsPage],
  );
  const hitsFrom =
    hitsPager.total === 0
      ? 0
      : (hitsPager.page - 1) * hitsPager.pageSize + 1;
  const hitsTo = Math.min(
    hitsPager.page * hitsPager.pageSize,
    hitsPager.total,
  );

  useEffect(() => {
    setHitsPage(1);
  }, [filters, queryParams, hits.events]);

  useEffect(() => {
    if (hitsPager.page !== hitsPage) {
      setHitsPage(hitsPager.page);
    }
  }, [hitsPager.page, hitsPage]);

  const displayTotals = hasFilters
    ? filteredTotals
    : summary.totals || emptySummary.totals;
  const rawTotals = summary.rawTotals || displayTotals;
  const countsCorrected = Boolean(summary.adjustment?.active) && !hasFilters;
  const placementCount = hasFilters
    ? filteredRows.length
    : (summary.rows || []).length;

  const previewUrl = useMemo(
    () =>
      buildUtmUrl({
        source: builder.source,
        medium: builder.medium,
        campaign: slugUtmValue(builder.campaign),
        content: builder.content,
      }),
    [builder],
  );

  const gabMode = builder.source === "gab";

  const knownCampaigns = useMemo(
    () => campaignChoices(summary.byCampaign),
    [summary.byCampaign],
  );
  const campaignIsKnown = knownCampaigns.some(
    (item) => item.id === builder.campaign,
  );
  const campaignSelectValue =
    campaignIsKnown && !builder.customCampaign
      ? builder.campaign
      : NEW_CAMPAIGN;
  const campaignPending =
    !gabMode && builder.customCampaign && !slugUtmValue(builder.campaign);

  const setFilterValue = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => setFilters({ ...EMPTY_FILTERS });

  const applyPreset = (nextDays) => {
    setDays(nextDays);
    const next = presetYmdRange(nextDays);
    setFromDate(next.from);
    setToDate(next.to);
  };

  const setCustomFrom = (value) => {
    setDays("custom");
    setFromDate(value);
  };

  const setCustomTo = (value) => {
    setDays("custom");
    setToDate(value);
  };

  const copyText = async (text, okMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(okMessage);
    } catch {
      toast.error("Could not copy. Select the text and copy it manually.");
    }
  };

  const handleExport = () => {
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadJson(summary, `ignite-elysium-tracking-${stamp}.json`);
    toast.success("Tracking JSON downloaded.");
  };

  const renderBreakdown = (title, items, filterKey) => (
    <div>
      <div className={local["breakdown-head"]}>
        <h3 className={styles["panel-title"]}>{title}</h3>
        <span className={local["breakdown-head-counts"]} aria-hidden="true">
          <span>Land</span>
          <span>Sign</span>
          <span>Conv</span>
        </span>
      </div>
      {items.length === 0 ? (
        <p className={styles["row-muted"]}>No data yet.</p>
      ) : (
        <div className={local["breakdown-list"]}>
          {items.map((item) => (
            <div key={item.id} className={local["breakdown-row"]}>
              <span className={local["breakdown-id"]}>
                {dimDisplay(filterKey, item.id)}
              </span>
              <span className={local["breakdown-counts"]}>
                <span>{formatCount(item.landings)}</span>
                <span>{formatCount(item.signups)}</span>
                <span className={local["breakdown-conv"]}>
                  {formatConversion(item.landings, item.signups)}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const filterSelects = [
    {
      key: "source",
      label: "Source",
      allLabel: "All sources",
      items: allView.bySource,
    },
    {
      key: "medium",
      label: "Medium",
      allLabel: "All mediums",
      items: allView.byMedium,
    },
    {
      key: "campaign",
      label: "Campaign",
      allLabel: "All campaigns",
      items: allView.byCampaign,
    },
    {
      key: "content",
      label: "Content",
      allLabel: "All content",
      items: allView.byContent,
    },
  ];

  return (
    <div>
      <div className={styles["page-header"]}>
        <div className={styles["page-header-content"]}>
          <h1 className={styles["page-title"]}>Tracking</h1>
          <p className={styles["page-subtitle"]}>
            Landings and founding-list joins from UTM links. No emails stored
            here.
          </p>
        </div>
        <div className={styles["page-actions"]}>
          <button
            type="button"
            className={styles["btn-secondary"]}
            onClick={load}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            type="button"
            className={styles["btn-secondary"]}
            onClick={handleExport}
            disabled={loading}
          >
            Export JSON
          </button>
        </div>
      </div>

      <AdminTrackBrowserSetting variant="tracking" />
      <AdminTrackCountAdjust
        summary={summary}
        days={days}
        user={user}
        onSummary={(data) => setSummary({ ...emptySummary, ...data })}
      />
      {error ? (
        <div className={styles.panel} style={{ marginBottom: "1.2rem" }}>
          <p className={styles["field-error"]}>{error}</p>
        </div>
      ) : null}

      <div className={styles["stats-grid"]}>
        <div className={styles["stat-card"]} data-stat="landings">
          <p className={styles["stat-label"]}>Landings</p>
          <p className={styles["stat-value"]}>
            {formatCount(displayTotals.landings)}
          </p>
          <p className={styles["stat-meta"]}>
            {hasFilters
              ? "this view"
              : countsCorrected
                ? `recorded ${formatCount(rawTotals.landings)}`
                : "sessions in range"}
          </p>
        </div>
        <div className={styles["stat-card"]} data-stat="signups">
          <p className={styles["stat-label"]}>Signups</p>
          <p className={styles["stat-value"]}>
            {formatCount(displayTotals.signups)}
          </p>
          <p className={styles["stat-meta"]}>
            {hasFilters
              ? "this view"
              : countsCorrected
                ? `recorded ${formatCount(rawTotals.signups)}`
                : "Kit joins in range"}
          </p>
        </div>
        <div className={styles["stat-card"]} data-stat="conversion">
          <p className={styles["stat-label"]}>Conversion</p>
          <p className={styles["stat-value"]}>
            {formatConversion(displayTotals.landings, displayTotals.signups)}
          </p>
          <p className={styles["stat-meta"]}>signups / landings</p>
        </div>
        <div className={styles["stat-card"]} data-stat="placements">
          <p className={styles["stat-label"]}>Connections</p>
          <p className={styles["stat-value"]}>{formatCount(placementCount)}</p>
          <p className={styles["stat-meta"]}>
            {hasFilters ? "matching rows" : "distinct UTM rows"}
          </p>
        </div>
      </div>

      <div className={`${styles.toolbar} ${local["filter-bar"]}`}>
        <div className={local["filter-field"]}>
          Range
          <div
            className={styles.segmented}
            role="group"
            aria-label="Date range"
          >
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={String(opt.id)}
                type="button"
                className={styles["segmented-btn"]}
                aria-pressed={days === opt.id}
                onClick={() => applyPreset(opt.id)}
                style={
                  days === opt.id
                    ? { background: "hsl(0 0% 100% / 0.12)", color: "#fff" }
                    : undefined
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <label className={local["filter-field"]}>
          From
          <input
            className={styles["text-input"]}
            type="date"
            value={fromDate}
            max={toDate || undefined}
            aria-label="From date"
            onChange={(e) => setCustomFrom(e.target.value)}
          />
        </label>
        <label className={local["filter-field"]}>
          To
          <input
            className={styles["text-input"]}
            type="date"
            value={toDate}
            min={fromDate || undefined}
            aria-label="To date"
            onChange={(e) => setCustomTo(e.target.value)}
          />
        </label>
        {filterSelects.map(({ key, label, allLabel, items }) => {
          const selected = filters[key];
          const options = items.slice();
          if (selected && !options.some((item) => item.id === selected)) {
            options.unshift({ id: selected, landings: 0, signups: 0 });
          }
          return (
            <label key={key} className={local["filter-field"]}>
              {label}
              <select
                className={styles["select-input"]}
                value={selected}
                aria-label={`Filter by ${label.toLowerCase()}`}
                onChange={(e) => setFilterValue(key, e.target.value)}
              >
                <option value="">{allLabel}</option>
                {options.map((item) => (
                  <option key={item.id} value={item.id}>
                    {dimDisplay(key, item.id)}
                  </option>
                ))}
              </select>
            </label>
          );
        })}
        {hasFilters ? (
          <button
            type="button"
            className={styles["btn-ghost"]}
            onClick={clearFilters}
          >
            Clear filters
          </button>
        ) : null}
      </div>

      <div className={styles.panel}>
        <h2 className={styles["panel-title"]}>Breakdown</h2>
        <p className={styles["field-hint"]} style={{ marginBottom: "1rem" }}>
          Landings, signups, and conversion for the current filters.
        </p>
        <div className={local["breakdown-grid"]}>
          {renderBreakdown("Source", sourceItems, "source")}
          {renderBreakdown("Medium", mediumItems, "medium")}
          {renderBreakdown("Campaign", campaignItems, "campaign")}
          {renderBreakdown("Content", contentItems, "content")}
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles["panel-header"]}>
          <h2 className={styles["panel-title"]}>Placements</h2>
        </div>
        {loading ? (
          <div className={styles["loading-block"]}>Loading tracking data…</div>
        ) : (summary.rows || []).length === 0 ? (
          <div className={styles["empty-state"]}>
            <h3>No tagged visits yet</h3>
            <p>
              Open a UTM link, or mint one with the builder below. Gab links
              only keep <code>utm_source</code>.
            </p>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className={styles["empty-state"]}>
            <h3>No visits match this view</h3>
            <p>Clear a filter, or choose All in the dropdowns to see every connection.</p>
          </div>
        ) : (
          <>
            <p
              className={styles["field-hint"]}
              style={{ marginBottom: "0.75rem" }}
            >
              {hasFilters ? "This view" : "All data"}:{" "}
              {formatCount(filteredTotals.landings)} landings ·{" "}
              {formatCount(filteredTotals.signups)} signups ·{" "}
              {formatConversion(
                filteredTotals.landings,
                filteredTotals.signups,
              )}
              . First and last are the earliest and most recent hits for that
              placement.
            </p>
            <div className={styles["table-wrap"]}>
              <table className={`${styles.table} ${local["view-table"]}`}>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Medium</th>
                    <th>Campaign</th>
                    <th>Content</th>
                    <th>Landings</th>
                    <th>Signups</th>
                    <th>Conv.</th>
                    <th>First</th>
                    <th>Last</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr
                      key={[
                        row.source,
                        row.medium,
                        row.campaign,
                        row.content,
                      ].join("|")}
                    >
                      <td className={styles["row-title"]}>
                        {dimDisplay("source", row.source)}
                      </td>
                      <td className={styles["row-muted"]}>
                        {dimDisplay("medium", row.medium)}
                      </td>
                      <td className={styles["row-muted"]}>
                        {dimDisplay("campaign", row.campaign)}
                      </td>
                      <td>{dimDisplay("content", row.content)}</td>
                      <td>{formatCount(row.landings)}</td>
                      <td>{formatCount(row.signups)}</td>
                      <td className={styles["row-muted"]}>
                        {formatConversion(row.landings, row.signups)}
                      </td>
                      <td className={styles["row-muted"]}>
                        {formatDateDisplay(row.firstAt)}
                      </td>
                      <td className={styles["row-muted"]}>
                        {formatDateDisplay(row.lastAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filteredRows.length > 1 ? (
                  <tfoot>
                    <tr>
                      <td className={styles["row-title"]}>Total</td>
                      <td colSpan={3} className={styles["row-muted"]}>
                        {formatCount(filteredRows.length)} placements
                      </td>
                      <td>{formatCount(filteredTotals.landings)}</td>
                      <td>{formatCount(filteredTotals.signups)}</td>
                      <td className={styles["row-muted"]}>
                        {formatConversion(
                          filteredTotals.landings,
                          filteredTotals.signups,
                        )}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </>
        )}
      </div>

      <div className={styles.panel}>
        <div className={styles["panel-header"]}>
          <h2 className={styles["panel-title"]}>Hits</h2>
        </div>
        {loading ? (
          <div className={styles["loading-block"]}>Loading tracking data…</div>
        ) : (hits.events || []).length === 0 ? (
          <div className={styles["empty-state"]}>
            <h3>No hits in this range</h3>
            <p>
              Each hit is one recorded landing or signup, with its date and
              the UTM values on that event.
            </p>
          </div>
        ) : filteredHits.length === 0 ? (
          <div className={styles["empty-state"]}>
            <h3>No hits match this view</h3>
            <p>Clear a filter, or widen the date range.</p>
          </div>
        ) : (
          <>
            <p
              className={styles["field-hint"]}
              style={{ marginBottom: "0.75rem" }}
            >
              {formatCount(filteredHits.length)}
              {hasFilters || hits.truncated ? " shown" : ""}
              {hits.truncated
                ? ` of ${formatCount(hits.total)} in range (newest ${formatCount(
                    (hits.events || []).length,
                  )})`
                : hasFilters
                  ? ` of ${formatCount((hits.events || []).length)} in range`
                  : ""}
              . Each row is one landing or signup.
            </p>
            <div className={styles["table-wrap"]}>
              <table className={`${styles.table} ${local["view-table"]}`}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Event</th>
                    <th>Source</th>
                    <th>Medium</th>
                    <th>Campaign</th>
                    <th>Content</th>
                    <th>Path</th>
                    <th>Session</th>
                  </tr>
                </thead>
                <tbody>
                  {hitsPager.items.map((hit) => (
                    <tr key={hit.id || `${hit.sessionId}|${hit.event}|${hit.createdAt}`}>
                      <td className={styles["row-title"]}>
                        {formatDateDisplay(hit.createdAt)}
                      </td>
                      <td>{eventLabel(hit.event)}</td>
                      <td className={styles["row-muted"]}>
                        {dimDisplay("source", hit.source)}
                      </td>
                      <td className={styles["row-muted"]}>
                        {dimDisplay("medium", hit.medium)}
                      </td>
                      <td className={styles["row-muted"]}>
                        {dimDisplay("campaign", hit.campaign)}
                      </td>
                      <td>{dimDisplay("content", hit.content)}</td>
                      <td className={styles["row-muted"]}>
                        {hit.path || "/"}
                      </td>
                      <td className={local["session-cell"]} title={hit.sessionId}>
                        {hit.sessionId || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hitsPager.totalPages > 1 ? (
              <nav className={styles.pagination} aria-label="Hits pages">
                <span>
                  {formatCount(hitsFrom)}–{formatCount(hitsTo)} of{" "}
                  {formatCount(hitsPager.total)}
                </span>
                <div className={styles["pagination-controls"]}>
                  <button
                    type="button"
                    className={styles["btn-ghost"]}
                    onClick={() => setHitsPage(hitsPager.page - 1)}
                    disabled={hitsPager.page <= 1}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  <span
                    className={styles["pagination-status"]}
                    aria-live="polite"
                  >
                    Page {formatCount(hitsPager.page)} of{" "}
                    {formatCount(hitsPager.totalPages)}
                  </span>
                  <button
                    type="button"
                    className={styles["btn-ghost"]}
                    onClick={() => setHitsPage(hitsPager.page + 1)}
                    disabled={hitsPager.page >= hitsPager.totalPages}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              </nav>
            ) : null}
          </>
        )}
      </div>

      <div className={styles.panel}>
        <h2 className={styles["panel-title"]}>Link builder</h2>
        <p className={styles["field-hint"]} style={{ marginBottom: "1rem" }}>
          Same rules Grok Bot uses. Pick an existing campaign, or New campaign
          and type a slug (lowercase, underscores). Email and file mediums are
          for Kit / Grok Bot links.
        </p>
        <div className={local["builder-grid"]}>
          <label className={styles.field}>
            Source
            <select
              className={styles["select-input"]}
              value={builder.source}
              onChange={(e) =>
                setBuilder((prev) => ({ ...prev, source: e.target.value }))
              }
            >
              {utmVocabulary.sources.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            Medium
            <select
              className={styles["select-input"]}
              value={builder.medium}
              disabled={gabMode}
              onChange={(e) =>
                setBuilder((prev) => ({ ...prev, medium: e.target.value }))
              }
            >
              {utmVocabulary.mediums.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            Campaign
            <select
              className={styles["select-input"]}
              value={gabMode ? builder.campaign : campaignSelectValue}
              disabled={gabMode}
              onChange={(e) => {
                const value = e.target.value;
                if (value === NEW_CAMPAIGN) {
                  setBuilder((prev) => ({
                    ...prev,
                    campaign: "",
                    customCampaign: true,
                  }));
                  return;
                }
                setBuilder((prev) => ({
                  ...prev,
                  campaign: value,
                  customCampaign: false,
                }));
              }}
            >
              {knownCampaigns.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
              <option value={NEW_CAMPAIGN}>New campaign…</option>
            </select>
            {campaignSelectValue === NEW_CAMPAIGN && !gabMode ? (
              <input
                className={styles["text-input"]}
                value={builder.campaign}
                onChange={(e) =>
                  setBuilder((prev) => ({
                    ...prev,
                    campaign: e.target.value,
                    customCampaign: true,
                  }))
                }
                onBlur={() =>
                  setBuilder((prev) => {
                    const slug = slugUtmValue(prev.campaign);
                    const known = knownCampaigns.some(
                      (item) => item.id === slug,
                    );
                    return {
                      ...prev,
                      campaign: slug,
                      customCampaign: !known,
                    };
                  })
                }
                placeholder="new_campaign"
                aria-label="New campaign slug"
              />
            ) : null}
          </label>
          <label className={styles.field}>
            Content
            <input
              className={styles["text-input"]}
              value={builder.content}
              disabled={gabMode}
              onChange={(e) =>
                setBuilder((prev) => ({ ...prev, content: e.target.value }))
              }
              placeholder="t-01"
            />
          </label>
        </div>
        {gabMode ? (
          <p
            className={styles["field-hint"]}
            style={{ marginBottom: "0.75rem" }}
          >
            Gab drops every parameter after the first. This URL is source only.
          </p>
        ) : null}
        {campaignPending ? (
          <p
            className={styles["field-hint"]}
            style={{ marginBottom: "0.75rem" }}
          >
            Type a campaign slug to finish the URL.
          </p>
        ) : null}
        <code className={local["url-preview"]}>{previewUrl}</code>
        <div
          className={styles["page-actions"]}
          style={{ marginTop: "0.85rem" }}
        >
          <button
            type="button"
            className={styles["btn-primary"]}
            onClick={() => copyText(previewUrl, "Link copied.")}
            disabled={campaignPending}
          >
            Copy link
          </button>
        </div>
      </div>

      <div className={styles.panel}>
        <h2 className={styles["panel-title"]}>Helm / Grok Bot JSON</h2>
        <p className={styles["field-hint"]}>
          Export above is the same payload as{" "}
          <code>GET /api/track/snapshot</code>. Put{" "}
          <code>TRACK_SNAPSHOT_TOKEN</code> in backend env (not git). Append{" "}
          <code>&amp;token=…</code> when fetching.
        </p>
        <code className={local["url-preview"]} style={{ marginTop: "0.75rem" }}>
          {trackSnapshotUrl(days, queryParams)}
        </code>
        <div
          className={styles["page-actions"]}
          style={{ marginTop: "0.85rem" }}
        >
          <button
            type="button"
            className={styles["btn-secondary"]}
            onClick={() =>
              copyText(
                trackSnapshotUrl(days, queryParams),
                "Snapshot path copied. Add the token from env.",
              )
            }
          >
            Copy snapshot path
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminTracking;
